import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import { dateToString } from "@shared/utils/DateUtils";
import { parseJsAddinfoObject } from "@shared/utils/AddInfoUtils";
import { getCodeData } from "@shared/utils/CommonUtils";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import FreeForm from "@widgets/FreeForm";
import { showDialog } from "@widgets/CommonDialog";

// SYS_ADDINFO_REG_POP_01
// - sys_addinfo 메타를 조회해 동적 입력 폼 생성
// - 입력값 검증 후 values 객체로 부모 모달에 반환
interface SysAddInfoRegPopup01Props {
  modalRef?: any;
  cd_menu: string;
  id_target: string;
  rowData: any;
}

type AddInfoMeta = {
  cd_flag: string;
  nm_flag?: string;
  tp_data?: string;
  yn_null?: string;
  no_sort?: number;
  dc_qt_valid?: string;
  cd_clas?: string;
};

type HeaderMeta = {
  cd_column?: string;
  nm_column?: string;
  cd_displaycolumn?: string;
  cd_clas?: string;
  tp_column?: string;
  dc_sql?: string;
  no_seq_p?: number;
  qt_size?: number;
};

const FORM_COLSPAN = 3;
const BASE_FORM_COLSPAN = 2;

// sys_addinfo.tp_data 값을 FreeForm 컨트롤 타입으로 매핑
const mapTpDataToCtrl = (tpData?: string) => {
  const tp = String(tpData ?? "")
    .trim()
    .toUpperCase();
  if (tp === "CODE") return "SnComboBox";
  if (tp === "VARCHA") return "SnTextBox";
  if (tp === "TEXT") return "SnMemoBox";
  if (tp === "NUMERI") return "SnNumberBox";
  if (tp === "DATE") return "SnDateBox";
  if (tp === "BOOL") return "SnCheckBox";
  return "SnTextBox";
};

// sys_addinfo_header.tp_column 값을 FreeForm 컨트롤 타입으로 정규화
const mapTpColumnToCtrl = (tpColumn?: string) => {
  const tp = String(tpColumn ?? "").trim();
  if (!tp) return "SnTextBox";
  if (tp.startsWith("Sn")) return tp;
  const upper = tp.toUpperCase();
  if (upper.includes("DATE")) return "SnDateBox";
  if (upper.includes("NUMBER") || upper.includes("NUMERIC"))
    return "SnNumberBox";
  if (upper.includes("MEMO") || upper.includes("TEXTAREA")) return "SnMemoBox";
  if (upper.includes("CHECK") || upper.includes("BOOL")) return "SnCheckBox";
  if (upper.includes("COMBO") || upper.includes("CODE")) return "SnComboBox";
  return "SnTextBox";
};

// 상단 Grid 컬럼 생성 시 tp_column을 그리드 tp_data 값으로 변환
const mapTpColumnToGridTpData = (tpColumn?: string) => {
  const tp = String(tpColumn ?? "")
    .trim()
    .toUpperCase();
  if (tp.includes("COMBO") || tp.includes("CODE")) return "CODE";
  if (tp.includes("DATE")) return "DATE";
  if (tp.includes("NUMBER") || tp.includes("NUMERIC")) return "NUMERI";
  if (tp.includes("CHECK") || tp.includes("BOOL")) return "BOOL";
  return "TEXT";
};

const SYS_ADDINFO_REG_POP_01 = forwardRef(
  (
    { modalRef, cd_menu, id_target, rowData }: SysAddInfoRegPopup01Props,
    ref: React.Ref<any>
  ) => {
    const { userInfo } = global();
    const formRef = useRef<any>(null);
    const baseGridRef = useRef<any>(null);
    const [controls, setControls] = useState<any[]>([]);
    const [formData, setFormData] = useState<any>({});
    const [dynamicKeys, setDynamicKeys] = useState<string[]>([]);
    const [displayFg, setDisplayFg] = useState<"G" | "F">("G");
    const [baseGridColumns, setBaseGridColumns] = useState<any[]>([]);
    const [baseGridRowData, setBaseGridRowData] = useState<any[]>([]);
    const [baseFormControls, setBaseFormControls] = useState<any[]>([]);
    const [baseFormData, setBaseFormData] = useState<any>({});
    const splitCdMenu = "SYS_ADDINFO_REG_POP_01";
    const splitDefaultSize = { panel1height: 260, panel2height: "100%" };
    const [splitSizes, onSplitResizeStop] = usePanelSizes(
      splitCdMenu,
      splitDefaultSize
    );
    const limitRulesRef = useRef<Record<string, { max: number; tp: string }>>(
      {}
    );
    const fitTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    // SnForm 최신값 우선 사용
    const getCurrentFormData = () =>
      formRef.current?.getFormdata?.() ?? formData;

    // 동적 키 목록 기준으로 반환 payload 구성
    const buildPopupValues = () => {
      const current = getCurrentFormData();
      const values: Record<string, any> = {};
      dynamicKeys.forEach((k) => {
        values[k] = current?.[k] ?? "";
      });
      return values;
    };

    const clearFitTimers = () => {
      fitTimersRef.current.forEach((t) => clearTimeout(t));
      fitTimersRef.current = [];
    };

    const fitBaseGridColumns = () => {
      clearFitTimers();
      const schedule = [0, 80, 180, 320];
      schedule.forEach((delay) => {
        const timer = setTimeout(() => {
          const grid = baseGridRef.current;
          if (!grid) return;
          grid.sizeColumnsToFit?.();
        }, delay);
        fitTimersRef.current.push(timer);
      });
    };

    // 검증 실패 시 해당 입력 컨트롤로 포커스를 이동
    const focusInvalidField = (cdParam: string) => {
      const targetIndex = controls.findIndex(
        (c) => String(c?.cd_param ?? "") === cdParam
      );
      if (targetIndex < 0) return;

      const popupRoot: HTMLElement | null =
        modalRef?.current?.getPopupParent?.() ?? null;
      if (!popupRoot) return;

      const rows =
        popupRoot.querySelectorAll<HTMLDivElement>(".flex.justify-end");
      const targetRow = rows?.[targetIndex];
      if (!targetRow) return;

      const target = targetRow.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
      );
      if (target) {
        target.focus();
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    // 팝업 진입 시 메타를 읽어 동적 폼/초기값을 구성
    useEffect(() => {
      const load = async () => {
        const rtn: any = await fnGetDataSet({
          id: "sys_addinfo_pop_01_01",
          params: {
            cd_firm: userInfo.cd_firm,
            cd_menu,
            id_target,
          },
        });

        // 표시방식(sys_addinfoh.fg_display) 로드
        const displayInfo = Array.isArray(rtn?.[1]) ? rtn[1]?.[0] : undefined;
        const nextDisplayFg = String(displayInfo?.fg_display ?? "G")
          .trim()
          .toUpperCase();
        setDisplayFg(nextDisplayFg === "F" ? "F" : "G");

        // 기준정보 표시 메타(sys_addinfo_header) 로드
        const headerMetas: HeaderMeta[] = Array.isArray(rtn?.[2]) ? rtn[2] : [];

        // 기준정보-Grid 컬럼 구성(읽기전용)
        const nextGridColumns = headerMetas.map((m, idx) => ({
          cd_grid: "grid_base",
          no_line: String(idx + 1),
          cd_column: String(m.cd_column ?? ""),
          nm_column: String(m.nm_column ?? m.cd_column ?? ""),
          // SnGrid가 콤보 로딩/렌더링을 하려면 tp_column 인식이 우선 필요하다.
          tp_column: String(m.tp_column ?? "").trim() || "SnTextBox",
          tp_data: mapTpColumnToGridTpData(m.tp_column),
          cd_clas: String(m.cd_clas ?? ""),
          dc_sql: String(m.dc_sql ?? ""),
          cd_displaycolumn: String(m.cd_displaycolumn ?? ""),
          fg_visible: "Y",
        }));

        // SnComboBox는 js_combo가 있어야 코드값이 아니라 명칭 렌더링이 된다.
        const nextGridColumnsWithCombo = await Promise.all(
          nextGridColumns.map(async (col: any) => {
            if (String(col.tp_column ?? "") !== "SnComboBox") return col;
            const comboRows = await getCodeData(
              String(col.cd_clas ?? ""),
              String(col.dc_sql ?? "")
            );
            return {
              ...col,
              js_combo: Array.isArray(comboRows) ? comboRows : [],
            };
          })
        );
        setBaseGridColumns(nextGridColumnsWithCombo);
        setBaseGridRowData(rowData ? [rowData] : []);

        // 기준정보-FreeForm 컨트롤 구성(읽기전용)
        const nextBaseControls = headerMetas.map((m, idx) => {
          const cdParam = String(m.cd_column ?? "").trim();
          return {
            no_line: String(5000 + idx),
            tp_ctrl: mapTpColumnToCtrl(m.tp_column),
            nm_label: String(m.nm_column ?? cdParam),
            cd_param: cdParam,
            cd_displaycolumn: String(m.cd_displaycolumn ?? ""),
            cd_clas: String(m.cd_clas ?? ""),
            dc_sql: String(m.dc_sql ?? ""),
            dc_editcon: "true",
            fg_visible: "Y",
            yn_required: "N",
            qt_rowspan: 1,
            qt_colspan: BASE_FORM_COLSPAN,
          };
        });
        setBaseFormControls(nextBaseControls);
        setBaseFormData(rowData ?? {});

        // cd_flag 기준으로 실제 표시할 부가항목 목록
        const metas: AddInfoMeta[] = (rtn?.[0] ?? [])
          .filter((m: any) => String(m?.cd_flag ?? "").trim() !== "")
          .sort(
            (a: any, b: any) =>
              Number(a?.no_sort ?? 0) - Number(b?.no_sort ?? 0)
          );

        const keys = metas.map((m) => String(m.cd_flag).trim());
        setDynamicKeys(keys);

        // 길이 검증 규칙(dc_qt_valid) 사전 구성
        const nextRules: Record<string, { max: number; tp: string }> = {};
        metas.forEach((m) => {
          const key = String(m.cd_flag ?? "").trim();
          const max = Number(m.dc_qt_valid ?? 0);
          if (!key || !Number.isFinite(max) || max <= 0) return;
          nextRules[key] = {
            max,
            tp: String(m.tp_data ?? "")
              .trim()
              .toUpperCase(),
          };
        });
        limitRulesRef.current = nextRules;

        // FreeForm controls 동적 생성
        const dynamicControls = metas.map((m, idx) => {
          const tpCtrl = mapTpDataToCtrl(m.tp_data);
          return {
            no_line: String(10000 + idx),
            tp_ctrl: tpCtrl,
            nm_label: String(m.nm_flag ?? m.cd_flag),
            cd_param: String(m.cd_flag),
            cd_clas: String(m.cd_clas ?? ""),
            fg_visible: "Y",
            yn_required: String(m.yn_null ?? "Y") === "N" ? "Y" : "N",
            // 메모 에디터는 3줄 높이로 표시한다.
            qt_rowspan: tpCtrl === "SnMemoBox" ? 3 : 1,
            qt_colspan: FORM_COLSPAN,
          };
        });

        // 초기값 우선순위:
        // 1) rowData[cd_flag]
        // 2) rowData.js_addinfo[cd_flag]
        // 3) 타입이 DATE면 오늘, 그 외 빈값
        const initial: any = {};
        const today = dateToString(new Date());
        const jsAddinfo = parseJsAddinfoObject(rowData?.js_addinfo);
        metas.forEach((m) => {
          const key = String(m.cd_flag ?? "").trim();
          if (!key) return;

          const fromRow = rowData?.[key];
          const fromJsAddinfo = jsAddinfo?.[key];
          const current =
            fromRow !== undefined && fromRow !== null && String(fromRow) !== ""
              ? fromRow
              : fromJsAddinfo;
          const tp = String(m.tp_data ?? "")
            .trim()
            .toUpperCase();
          const isDateType = tp.includes("DATE");
          initial[key] =
            current !== undefined && current !== null && String(current) !== ""
              ? current
              : isDateType
                ? today
                : "";
        });
        setFormData(initial);
        setControls(dynamicControls);
      };

      load();
    }, [cd_menu, id_target, userInfo.cd_firm, rowData]);

    useEffect(() => {
      if (displayFg !== "G") return;
      fitBaseGridColumns();
      return () => clearFitTimers();
    }, [displayFg, baseGridColumns, baseGridRowData]);

    // CommonModal validation/confirm 규약에 맞춘 핸들 노출
    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        const current = getCurrentFormData();
        // 1차: 필수값 검증
        for (const c of controls) {
          if (c.yn_required !== "Y") continue;
          const v = current?.[c.cd_param];
          if (v === undefined || v === null || String(v).trim() === "") {
            await showDialog("alter", `${c.nm_label}은(는) 필수값입니다.`);
            requestAnimationFrame(() => focusInvalidField(String(c.cd_param)));
            return false;
          }
        }

        // 2차: 길이 제한 검증
        for (const c of controls) {
          const key = String(c?.cd_param ?? "");
          const label = String(c?.nm_label ?? key);
          const rule = limitRulesRef.current[key];
          if (!rule || rule.max <= 0 || rule.tp === "BOOL") continue;

          const raw = current?.[key];
          if (raw === undefined || raw === null || raw === "") continue;
          const text = String(raw);
          if (text.length > rule.max) {
            await showDialog(
              "alter",
              `${label}은(는) ${rule.max}자리 이하로 입력해 주세요.`
            );
            requestAnimationFrame(() => focusInvalidField(key));
            return false;
          }
        }
        return true;
      },
      getSelectedValue: async () => {
        // 일반 팝업 규약 대응
        return { values: buildPopupValues() };
      },
      getOkayData: async () => {
        // 리사이즈 팝업 규약 대응
        const values = buildPopupValues();
        return {
          closeFlag: true,
          rtnData: { values },
        };
      },
    }));

    return (
      <div className="h-full min-h-0 flex flex-col overflow-hidden">
        <SnResizable
          direction={"height"}
          seq={1}
          cd_menu={splitCdMenu}
          defaultSize={splitDefaultSize}
          size={splitSizes.panel1height}
          onResizeStop={onSplitResizeStop}
          enable={{ bottom: true }}
          minHeight={180}
        >
          <div className="h-full min-h-0 flex flex-col">
            <SnTab
              titles={["기준정보"]}
              width="100%"
              height="100%"
              body={[
                displayFg === "G" ? (
                  <SnGrid
                    ref={baseGridRef}
                    columnConfig={baseGridColumns}
                    rowData={baseGridRowData}
                    popupParent={
                      modalRef?.current?.getPopupParent?.() || undefined
                    }
                    etcProps={{
                      isEditable: false,
                      rowSelection: "single",
                    }}
                    events={{
                      onRowDataReady: fitBaseGridColumns,
                    }}
                  />
                ) : (
                  <div className="h-full min-h-0 p-1">
                    <div className="h-full min-h-0 border border-gray-300 overflow-hidden">
                      <FreeForm
                        data={baseFormData}
                        controls={baseFormControls}
                        popupRef={modalRef}
                        fitToPopup={true}
                      />
                    </div>
                  </div>
                ),
              ]}
            />
          </div>
        </SnResizable>
        <div className="flex-1 min-h-0 mt-1">
          <SnTab
            titles={["부가정보"]}
            width="100%"
            height="100%"
            body={[
              <div className="h-full min-h-0 p-1">
                <div className="h-full min-h-0 border border-gray-300 overflow-auto">
                  <FreeForm
                    ref={formRef}
                    data={formData}
                    controls={controls}
                    popupRef={modalRef}
                    fitToPopup={false}
                  />
                </div>
              </div>,
            ]}
          />
        </div>
      </div>
    );
  }
);

export default SYS_ADDINFO_REG_POP_01;
